import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { Category } from '../../core/models/category.model';
import { CategoryService } from '../../core/services/category.service';
import { EventService } from '../../core/services/event.service';
import { SponsorService } from '../../core/services/sponsor.service';
import { Sponsor } from '../../core/models/sponsor.model';
import { RadicalEvent } from '../../core/models/event.model';
import { Observable } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EventSubscriptionDialogComponent } from '../events/components/event-subscription-dialog/event-subscription-dialog.component';
import { BrDatePipe } from '../../shared/pipes/br-date.pipe';
import { toShortBrDateParts } from '../../core/utils/date.util';
import { eventPath, sponsorLink } from '../../core/utils/event-link.util';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatDialogModule,
    BrDatePipe
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  readonly eventPath = eventPath;
  readonly sponsorLink = sponsorLink;

  searchForm: FormGroup;
  
  categories: Category[] = [];
  
  sponsors: Sponsor[] = [];

  featuredEvents$: Observable<RadicalEvent[]>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private eventService: EventService,
    private categoryService: CategoryService,
    private sponsorService: SponsorService,
    private dialog: MatDialog
  ) {
    this.searchForm = this.fb.group({
      query: [''],
      location: [''],
      date: [null]
    });
    this.featuredEvents$ = this.eventService.getEvents();
  }

  subscribeToEvent(event: RadicalEvent) {
    this.dialog.open(EventSubscriptionDialogComponent, {
      maxWidth: '550px', width: '95vw',
      data: { event },
      panelClass: 'glass-dialog'
    });
  }

  ngOnInit() {
    this.categoryService.getCategories().subscribe(cats => this.categories = cats);
    this.sponsorService.getActiveSponsors().subscribe(sponsors => {
      this.sponsors = sponsors;
    });
  }

  onSearch() {
    const formValue = this.searchForm.value;
    const queryParams: any = {};
    if (formValue.query) queryParams.query = formValue.query;
    if (formValue.location) queryParams.location = formValue.location;
    if (formValue.date) {
      const d = new Date(formValue.date);
      queryParams.date = d.toISOString().split('T')[0];
    }
    this.router.navigate(['/events'], { queryParams });
  }

  filterByCategory(category: string) {
    this.router.navigate(['/events'], { queryParams: { category } });
  }

  /**
   * O preço só sai do card quando a inscrição realmente fechou (janela vencida ou
   * evento encerrado/cancelado). Evento APPROVED ainda vai abrir inscrição, então
   * continua mostrando "A partir de".
   */
  isRegistrationClosed(event: RadicalEvent): boolean {
    if (event.status === 'CLOSED' || event.status === 'CANCELLED' || event.status === 'SUSPENDED') {
      return true;
    }
    return !!event.registrationEndDate && new Date() > new Date(event.registrationEndDate);
  }

  getEventDay(event: RadicalEvent): string {
    return toShortBrDateParts(event.date)?.day ?? '--';
  }

  getEventMonth(event: RadicalEvent): string {
    return toShortBrDateParts(event.date)?.month ?? '';
  }

  getRegistrationButtonText(event: RadicalEvent): string {
    const status = this.getRegistrationStatus(event);
    if (status === 'not_started') return 'INSCRIÇÕES EM BREVE';
    if (status === 'closed') return 'INSCRIÇÕES ENCERRADAS';
    if (status === 'sold_out') return 'EVENTO LOTADO';
    if (event.registered) return 'INSCRITO';
    return 'GARANTIR VAGA';
  }
  
  getRegistrationStatus(event: RadicalEvent): 'not_started' | 'open' | 'closed' | 'sold_out' {
    if (event.status && event.status !== 'OPEN') return 'closed';
    const now = new Date();
    if (event.registrationStartDate && now < new Date(event.registrationStartDate)) return 'not_started';
    if (event.registrationEndDate && now > new Date(event.registrationEndDate)) return 'closed';
    
    // Pendentes de pagamento também ocupam vaga (occupiedSpots).
    const occupied = event.occupiedSpots ?? event.confirmedRegistrations ?? 0;
    if (event.capacity && event.capacity > 0 && occupied >= event.capacity) {
      return 'sold_out';
    }
    
    return 'open';
  }

  isRegistrationDisabled(event: RadicalEvent): boolean {
    return this.getRegistrationStatus(event) !== 'open' || !!event.registered;
  }
}
